import {inject, Injectable} from '../di';
import {AppInitializeService} from '../app-initialize-service';
import {BehaviorSubject, skip} from 'rxjs';
import {I18nService} from './i18n-service';
import {Logger} from '../logger';
import moment from 'moment-timezone';
import {momentLocaleLoaders} from './moment-locales';

@Injectable()
export class MomentLocaleService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly i18nService = inject(I18nService);

  public readonly momentLocale$ = new BehaviorSubject<string>('en');

  async init(): Promise<boolean> {
    try {
      this.i18nService.localeFallbackChain$.pipe(skip(1)).subscribe(() => void this.loadMomentLocale());
      await this.loadMomentLocale();
    } catch (err) {
      this.logger.error('[MomentLocaleService] Unable to load Moment locale', err);
    }
    return true;
  }

  private async loadMomentLocale(): Promise<void> {
    const momentLocales = Object.keys(momentLocaleLoaders);
    momentLocales.push('en'); // predefined locale
    momentLocales.push('en-us'); // predefined locale
    let momentLocale = this.i18nService.getMostApplicableLocale(momentLocales) || 'en';
    if (momentLocale === 'en' || momentLocale === 'en-us') {
      this.logger.debug(`[MomentLocaleService] Loading moment locale "en"...`);
      moment.locale('en');
      this.momentLocale$.next('en');
      return;
    } else if (momentLocale === 'si') {
      // special case si -> sl, it's a bug of some old installations, someone used `si` instead of `sl` for Slovenian language
      momentLocale = 'sl';
    }
    this.logger.debug(`[MomentLocaleService] Loading moment locale "${momentLocale}"...`);
    await momentLocaleLoaders[momentLocale]();
    this.momentLocale$.next(momentLocale);
  }
}
